import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from './auth';
import { can } from './permissions';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  CheckCircle2,
  Film,
  Image as ImageIcon,
  LoaderCircle,
  Maximize2,
  Megaphone,
  MonitorPlay,
  Plus,
  ScanLine,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import type { Html5Qrcode as Html5QrcodeType } from "html5-qrcode";
import { api, post } from "./api";
import {TvAudioSettings} from './tv-audio';

export type TvItemType = "DASHBOARD" | "VIDEO" | "SLIDESHOW" | "LED";

export type TvMedia = {
  id: string;
  media_type: "IMAGE" | "VIDEO";
  original_name: string;
  mime_type: string;
  file_size: string | number;
  created_at: string;
  url: string;
};

export type TvScenarioItem = {
  id?: string;
  key?: string;
  type: TvItemType;
  title: string;
  durationSeconds: number;
  duration_seconds?: number;
  sort_order?: number;
  config: Record<string, any>;
};

export type TvScenario = {
  id: string;
  name: string;
  is_active: boolean;
  updated_at: string;
  items: TvScenarioItem[];
  media: TvMedia[];
};

const itemLabels: Record<TvItemType, string> = {
  DASHBOARD: "Tổng quan realtime",
  VIDEO: "Clip quảng cáo",
  SLIDESHOW: "Slideshow ảnh",
  LED: "Chữ LED chào mừng",
};

const itemIcons: Record<TvItemType, React.ReactNode> = {
  DASHBOARD: <BarChart3 />,
  VIDEO: <Film />,
  SLIDESHOW: <ImageIcon />,
  LED: <Megaphone />,
};

const ledColorPresets = [
  {
    id: "phuoc-hao",
    name: "Phước Hào",
    description: "Xanh thương hiệu",
    color: "#ffffff",
    background: "#123c96",
  },
  {
    id: "classic-led",
    name: "LED truyền thống",
    description: "Vàng trên nền đen",
    color: "#ffd43b",
    background: "#02070a",
  },
  {
    id: "welcome",
    name: "Chào mừng",
    description: "Vàng kem trang trọng",
    color: "#fff1b8",
    background: "#781c2e",
  },
  {
    id: "event",
    name: "Sự kiện",
    description: "Trắng tím nổi bật",
    color: "#ffffff",
    background: "#5426a8",
  },
  {
    id: "fresh",
    name: "Thân thiện",
    description: "Xanh lá dịu mắt",
    color: "#e4fff1",
    background: "#07543d",
  },
  {
    id: "technology",
    name: "Công nghệ",
    description: "Xanh băng hiện đại",
    color: "#dff8ff",
    background: "#003b5c",
  },
  {
    id: "energy",
    name: "Năng lượng",
    description: "Cam ấm mạnh mẽ",
    color: "#fff4d6",
    background: "#8a3100",
  },
  {
    id: "mono",
    name: "Tối giản",
    description: "Trắng đen rõ nét",
    color: "#ffffff",
    background: "#111827",
  },
] as const;

const normalizeScenario = (scenario: TvScenario): TvScenario => ({
  ...scenario,
  items: (scenario.items || []).map((item) => ({
    ...item,
    key: item.id || crypto.randomUUID(),
    durationSeconds: Number(item.duration_seconds ?? item.durationSeconds ?? 15),
    config: item.config || {},
  })),
});

const bytes = (value: string | number) => {
  const size = Number(value || 0);
  if (size >= 1024 * 1024)
    return `${(size / 1024 / 1024).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

function TvSceneFrame({
  scenario,
  item,
  index,
  total,
  onExit,
  children,
}: {
  scenario: TvScenario;
  item: TvScenarioItem;
  index: number;
  total: number;
  onExit?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="tv-scene-layer">
      <div className="tv-scene-content">{children}</div>
      <header className="tv-scene-header">
        <img src="/brand/phuoc-hao-horizontal.png" alt="Phước Hào" />
        <div>
          <b>{item.title}</b>
          <span>
            {scenario.name} · Cảnh {index + 1}/{total}
          </span>
        </div>
      </header>
      {onExit && (
        <button className="tv-exit-button" onClick={onExit}>
          <X /> Thoát xem thử
        </button>
      )}
      <div className="tv-scene-progress">
        {scenario.items.map((scene, sceneIndex) => (
          <span
            key={scene.id || scene.key || sceneIndex}
            className={sceneIndex === index ? "active" : ""}
          />
        ))}
      </div>
    </div>
  );
}

export function TvPlaybackLayer({
  active,
  scenario,
  onExit,
  preview = false,
  duckAudio = false,
}: {
  active: boolean;
  scenario: TvScenario | null | undefined;
  onExit: () => void;
  preview?: boolean;
  duckAudio?: boolean;
}) {
  const normalized = useMemo(
    () => (scenario ? normalizeScenario(scenario) : null),
    // Dashboard data refreshes every 15 seconds and returns a new scenario
    // object even when the playlist is unchanged. Keep scene identities stable
    // so those refreshes cannot restart a 30/50-second playback timer.
    // Admin preview must still reflect unsaved form edits immediately.
    [preview ? scenario : null, scenario?.id, scenario?.updated_at],
  );
  const [index, setIndex] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const [singleSceneFinished, setSingleSceneFinished] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const onExitRef = useRef(onExit);
  const items = normalized?.items || [];
  const item = items[index];
  const media = useMemo(
    () => new Map((normalized?.media || []).map((entry) => [entry.id, entry])),
    [normalized],
  );
  const advance = useCallback(() => {
    if (!items.length) return;
    if (items.length === 1) {
      if (preview) onExitRef.current();
      else setSingleSceneFinished(true);
      return;
    }
    setIndex((current) => (current + 1) % items.length);
    setCycle((value) => value + 1);
  }, [items.length, preview]);

  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);

  useEffect(() => {
    setIndex(0);
    setSingleSceneFinished(false);
    setCycle((value) => value + 1);
  }, [active, normalized?.id, normalized?.updated_at]);

  useEffect(() => {
    setSlideIndex(0);
    if (!active || !item) return;
    const missingVideo =
      item.type === "VIDEO" && !media.get(item.config.mediaId);
    const seconds = missingVideo
      ? 10
      : item.type === "VIDEO" && item.durationSeconds === 0
        ? 0
        : Math.max(5, item.durationSeconds || 15);
    if (!seconds) return;
    const timer = window.setTimeout(advance, seconds * 1000);
    return () => window.clearTimeout(timer);
  }, [active, advance, cycle, item, media]);

  useEffect(() => {
    if (!active || item?.type !== "SLIDESHOW") return;
    const imageIds = item.config.mediaIds || [];
    if (imageIds.length < 2) return;
    const timer = window.setInterval(
      () => setSlideIndex((current) => (current + 1) % imageIds.length),
      Math.max(2, Number(item.config.secondsPerImage || 6)) * 1000,
    );
    return () => window.clearInterval(timer);
  }, [active, cycle, item]);

  useEffect(() => {
    if(videoRef.current)videoRef.current.volume=duckAudio?0.15:1;
  }, [duckAudio, active, cycle, item]);

  useEffect(() => {
    if (!active || item?.type !== "VIDEO" || !videoRef.current) return;
    const player = videoRef.current;
    player.currentTime = 0;
    void player.play().catch(() => {
      player.muted = true;
      void player.play().catch(() => {});
    });
  }, [active, cycle, item]);

  if (!active) return null;
  // A one-scene announcement is temporary. Once its configured duration ends,
  // reveal the realtime dashboard instead of restarting the same scene forever.
  if (singleSceneFinished && !preview) return null;
  if (!normalized || !item) {
    return (
      <div className="tv-scene-layer tv-empty-scene">
        <MonitorPlay />
        <h1>Chưa có kịch bản TV đang hoạt động</h1>
        <p>Admin có thể tạo và kích hoạt kịch bản trong phần Kịch bản TV.</p>
        {preview && (
          <button className="primary" onClick={onExit}>
            Thoát xem thử
          </button>
        )}
      </div>
    );
  }

  if (item.type === "DASHBOARD" && !preview) return null;

  if (item.type === "DASHBOARD")
    return (
      <TvSceneFrame
        scenario={normalized}
        item={item}
        index={index}
        total={items.length}
        onExit={preview ? onExit : undefined}
      >
        <div className="tv-dashboard-preview">
          <span>TRUNG TÂM ĐIỀU HÀNH REALTIME</span>
          <h1>Tổng quan sản xuất</h1>
          <div className="tv-preview-kpis">
            {["Tổng máy", "Đang chạy", "Sẵn sàng", "Lệnh trễ", "Chưa ACK", "Sự cố"].map(
              (label, position) => (
                <div key={label}>
                  <b>{[12, 7, 5, 0, 2, 1][position]}</b>
                  <span>{label}</span>
                </div>
              ),
            )}
          </div>
          <div className="tv-preview-panels">
            <div />
            <div />
          </div>
        </div>
      </TvSceneFrame>
    );

  if (item.type === "VIDEO") {
    const clip = media.get(item.config.mediaId);
    return (
      <TvSceneFrame
        scenario={normalized}
        item={item}
        index={index}
        total={items.length}
        onExit={preview ? onExit : undefined}
      >
        {clip ? (
          <video
            key={`${item.id || item.key}-${cycle}`}
            ref={videoRef}
            className={`tv-video ${String(item.config.fit || "CONTAIN").toLowerCase()}`}
            src={clip.url}
            autoPlay
            playsInline
            muted={Boolean(item.config.muted)}
            onEnded={advance}
          />
        ) : (
          <div className="tv-missing-media">
            <Film />
            <h2>Không tìm thấy clip đã chọn</h2>
          </div>
        )}
      </TvSceneFrame>
    );
  }

  if (item.type === "SLIDESHOW") {
    const imageIds = item.config.mediaIds || [];
    const image = media.get(imageIds[slideIndex % Math.max(1, imageIds.length)]);
    return (
      <TvSceneFrame
        scenario={normalized}
        item={item}
        index={index}
        total={items.length}
        onExit={preview ? onExit : undefined}
      >
        {image ? (
          <img
            key={`${image.id}-${slideIndex}`}
            className={`tv-slide-image ${String(item.config.effect || "FADE").toLowerCase()} ${String(item.config.fit || "CONTAIN").toLowerCase()}`}
            src={image.url}
            alt={image.original_name}
          />
        ) : (
          <div className="tv-missing-media">
            <ImageIcon />
            <h2>Không tìm thấy ảnh trình chiếu</h2>
          </div>
        )}
        <div className="tv-slide-counter">
          {Math.min(slideIndex + 1, imageIds.length)}/{imageIds.length}
        </div>
      </TvSceneFrame>
    );
  }

  const speed =
    item.config.speed === "SLOW"
      ? "32s"
      : item.config.speed === "FAST"
        ? "12s"
        : "20s";
  // Vietnamese input can arrive as decomposed Unicode (base letter + accents),
  // which some TV/browser font stacks render as detached or missing marks.
  const ledText = String(item.config.text || "").normalize("NFC");
  return (
    <TvSceneFrame
      scenario={normalized}
      item={item}
      index={index}
      total={items.length}
      onExit={preview ? onExit : undefined}
    >
      <div
        className="tv-led-scene"
        style={{
          background: item.config.background || "#02070a",
          color: item.config.color || "#ffd43b",
        }}
      >
        <div className="tv-led-board">
          <div className="tv-led-track" style={{ animationDuration: speed }}>
            <span lang="vi">{ledText}</span>
          </div>
        </div>
        <img src="/brand/phuoc-hao-horizontal.png" alt="Phước Hào" />
      </div>
    </TvSceneFrame>
  );
}

const newItem = (type: TvItemType, media: TvMedia[]): TvScenarioItem | null => {
  const key = crypto.randomUUID();
  if (type === "DASHBOARD")
    return {
      key,
      type,
      title: "Tổng quan sản xuất realtime",
      durationSeconds: 30,
      config: {},
    };
  if (type === "VIDEO") {
    const firstVideo = media.find((item) => item.media_type === "VIDEO");
    if (!firstVideo) return null;
    return {
      key,
      type,
      title: firstVideo.original_name,
      durationSeconds: 0,
      config: { mediaId: firstVideo.id, muted: false, fit: "CONTAIN" },
    };
  }
  if (type === "SLIDESHOW") {
    const firstImage = media.find((item) => item.media_type === "IMAGE");
    if (!firstImage) return null;
    return {
      key,
      type,
      title: "Hình ảnh Phước Hào",
      durationSeconds: 30,
      config: {
        mediaIds: [firstImage.id],
        secondsPerImage: 6,
        effect: "FADE",
        fit: "CONTAIN",
      },
    };
  }
  return {
    key,
    type,
    title: "Chào mừng",
    durationSeconds: 20,
    config: {
      text: "PHƯỚC HÀO TRÂN TRỌNG CHÀO MỪNG QUÝ ĐỐI TÁC",
      color: "#ffd43b",
      background: "#02070a",
      speed: "NORMAL",
    },
  };
};

export function TvScenarioAdmin() {
  const { user } = useAuth();
  const canManage=can(user,'tv.manage'),canPublish=can(user,'tv.publish'),canMedia=can(user,'tv.media');
  const [scenarios, setScenarios] = useState<TvScenario[]>([]);
  const [media, setMedia] = useState<TvMedia[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<TvScenario | null>(null);
  const [newName, setNewName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(false);
  const [normalMode, setNormalMode] = useState(false);
  const [modeBusy, setModeBusy] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [pairScanning, setPairScanning] = useState(false);
  const [pairBusy, setPairBusy] = useState(false);
  const [pairMessage, setPairMessage] = useState("");
  const [pairError, setPairError] = useState("");
  const pairScanner = useRef<Html5QrcodeType | null>(null);
  const pairResolved = useRef(false);
  const pairClaiming = useRef(false);
  const stopPairScanner = useCallback(async () => {
    const scanner = pairScanner.current;
    pairScanner.current = null;
    if (!scanner) return;
    try { if (scanner.isScanning) await scanner.stop(); } catch { /* Already stopped. */ }
    try { scanner.clear(); } catch { /* Reader may have unmounted. */ }
  }, []);
  useEffect(() => () => { void stopPairScanner(); }, [stopPairScanner]);
  const pairingCodeFromQr = (raw: string) => {
    const url = new URL(raw);
    if (url.origin !== window.location.origin || url.pathname !== "/tv/pair")
      throw new Error("Đây không phải mã QR kết nối TV Phước Hào.");
    const code = (url.searchParams.get("code") || "").toUpperCase();
    if (!/^[A-HJ-NP-Z2-9]{6}$/.test(code)) throw new Error("Mã kết nối trên QR không hợp lệ.");
    return code;
  };
  const claimPairCode = async (code: string) => {
    if (!canPublish || pairClaiming.current) return;
    if (!/^[A-HJ-NP-Z2-9]{6}$/.test(code)) {
      setPairError("Hãy nhập đúng mã 6 ký tự đang hiển thị trên TV.");
      return;
    }
    pairClaiming.current = true;
    setPairBusy(true); setPairError(""); setPairMessage("");
    try {
      await post(`/tv/pairings/${encodeURIComponent(code)}/claim`);
      setPairCode("");
      setPairMessage("Đã kết nối. TV sẽ tự chuyển sang trình chiếu trong vài giây.");
    } catch (error) { setPairError((error as Error).message); }
    finally { pairClaiming.current = false; setPairBusy(false); }
  };
  const startPairScan = async () => {
    if (!canPublish || pairScanning || pairBusy) return;
    setPairError(""); setPairMessage(""); setPairScanning(true);
    pairResolved.current = false;
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("tv-pair-reader");
      pairScanner.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } }, raw => {
        if (pairResolved.current) return;
        try {
          const code = pairingCodeFromQr(raw);
          pairResolved.current = true;
          setPairCode(code);
          void stopPairScanner().then(() => setPairScanning(false));
          void claimPairCode(code);
        } catch (error) { setPairError((error as Error).message); }
      }, () => {});
    } catch {
      setPairScanning(false);
      setPairError("Không mở được camera. Hãy cấp quyền camera hoặc nhập mã hiển thị trên TV.");
      await stopPairScanner();
    }
  };
  const submitPairCode = (event: FormEvent) => {
    event.preventDefault();
    void claimPairCode(pairCode.trim().toUpperCase());
  };

  const load = useCallback(async (preferredId?: string) => {
    const [scenarioRows, mediaRows, kioskMode] = await Promise.all([
      api<TvScenario[]>("/tv/scenarios"),
      api<TvMedia[]>("/tv/media"),
      api<{ normalMode: boolean }>("/tv/kiosk-mode"),
    ]);
    const normalized = scenarioRows.filter(Boolean).map(normalizeScenario);
    setScenarios(normalized);
    setMedia(mediaRows);
    setNormalMode(kioskMode.normalMode);
    const nextId =
      preferredId ||
      (selectedId && normalized.some((item) => item.id === selectedId)
        ? selectedId
        : normalized[0]?.id || "");
    setSelectedId(nextId);
    setDraft(
      normalized.find((item) => item.id === nextId)
        ? structuredClone(normalized.find((item) => item.id === nextId)!)
        : null,
    );
  }, [selectedId]);

  useEffect(() => {
    void load().catch((caught) => setError((caught as Error).message));
  }, []);

  const chooseScenario = (id: string) => {
    setSelectedId(id);
    const found = scenarios.find((item) => item.id === id);
    setDraft(found ? structuredClone(found) : null);
    setError("");
    setNotice("");
  };

  const createScenario = async (event: FormEvent) => {
    event.preventDefault();
    if (!newName.trim() || busy) return;
    setBusy("create");
    setError("");
    try {
      const created = await post<TvScenario>("/tv/scenarios", {
        name: newName.trim(),
      });
      setNewName("");
      await load(created.id);
      setNotice("Đã tạo kịch bản mới với cảnh realtime mặc định.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy("");
    }
  };

  const updateItem = (position: number, change: Partial<TvScenarioItem>) => {
    if (!draft) return;
    const items = [...draft.items];
    items[position] = { ...items[position], ...change };
    setDraft({ ...draft, items });
  };

  const updateConfig = (position: number, change: Record<string, any>) => {
    if (!draft) return;
    updateItem(position, {
      config: { ...draft.items[position].config, ...change },
    });
  };

  const addItem = (type: TvItemType) => {
    if (!draft) return;
    const item = newItem(type, media);
    if (!item) {
      setError(
        type === "VIDEO"
          ? "Hãy tải ít nhất một video lên thư viện trước."
          : "Hãy tải ít nhất một ảnh lên thư viện trước.",
      );
      return;
    }
    setDraft({ ...draft, items: [...draft.items, item] });
    setError("");
  };

  const moveItem = (position: number, direction: -1 | 1) => {
    if (!draft) return;
    const target = position + direction;
    if (target < 0 || target >= draft.items.length) return;
    const items = [...draft.items];
    [items[position], items[target]] = [items[target], items[position]];
    setDraft({ ...draft, items });
  };

  const save = async () => {
    if (!draft || busy) return null;
    setBusy("save");
    setError("");
    setNotice("");
    try {
      const saved = await api<TvScenario>(`/tv/scenarios/${draft.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: draft.name,
          items: draft.items.map((item) => ({
            type: item.type,
            title: item.title,
            durationSeconds: Number(item.durationSeconds),
            config: item.config,
          })),
        }),
      });
      await load(saved.id);
      setNotice("Đã lưu kịch bản. Màn hình TV đang mở sẽ tự đồng bộ.");
      return saved;
    } catch (caught) {
      setError((caught as Error).message);
      return null;
    } finally {
      setBusy("");
    }
  };

  const activate = async () => {
    if (!draft || busy) return;
    const saved = canManage ? await save() : draft;
    if (!saved) return;
    setBusy("activate");
    try {
      await post(`/tv/scenarios/${saved.id}/activate`);
      await load(saved.id);
      setNotice("Kịch bản đã được kích hoạt cho màn hình TV.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy("");
    }
  };

  const removeScenario = async () => {
    if (!draft || draft.is_active || busy) return;
    if (!window.confirm(`Xóa kịch bản “${draft.name}”?`)) return;
    setBusy("delete");
    try {
      await api(`/tv/scenarios/${draft.id}`, { method: "DELETE" });
      await load();
      setNotice("Đã xóa kịch bản.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy("");
    }
  };

  const upload = async (event: FormEvent) => {
    event.preventDefault();
    if (!uploadFile || busy) return;
    const form = new FormData();
    form.append("file", uploadFile);
    setBusy("upload");
    setError("");
    try {
      await api("/tv/media", { method: "POST", body: form });
      setUploadFile(null);
      await load(selectedId);
      setNotice("Đã tải tệp vào thư viện TV.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy("");
    }
  };

  const removeMedia = async (entry: TvMedia) => {
    if (busy || !window.confirm(`Xóa tệp “${entry.original_name}”?`)) return;
    setBusy(`media-${entry.id}`);
    setError("");
    try {
      await api(`/tv/media/${entry.id}`, { method: "DELETE" });
      await load(selectedId);
      setNotice("Đã xóa tệp khỏi thư viện.");
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy("");
    }
  };

  const updateNormalMode = async (checked: boolean) => {
    if (
      checked &&
      !window.confirm(
        "Đưa TV về launcher Google TV? Phước Hào TV sẽ ngừng giữ màn hình kiosk cho đến khi bạn bỏ chọn mục này.",
      )
    ) return;
    setModeBusy(true);
    setError("");
    try {
      const saved = await api<{ normalMode: boolean }>("/tv/kiosk-mode", {
        method: "PATCH",
        body: JSON.stringify({ normalMode: checked }),
      });
      setNormalMode(saved.normalMode);
      setNotice(
        saved.normalMode
          ? "Đã yêu cầu TV trở về launcher mặc định."
          : "Đã bật lại chế độ kiosk Phước Hào TV.",
      );
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setModeBusy(false);
    }
  };

  return (
    <div className="page tv-admin-page">
      <div className="page-title">
        <div>
          <span className="eyebrow">DIGITAL SIGNAGE</span>
          <h1>Kịch bản trình chiếu TV</h1>
          <p className="page-subtitle">
            Trộn dữ liệu sản xuất realtime, clip, hình ảnh và bảng chữ chào mừng
            thành một playlist tự chạy.
          </p>
        </div>
        <div className="tv-admin-actions">
          <label className={`tv-normal-mode-toggle ${normalMode ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={normalMode}
              disabled={modeBusy || !canPublish}
              onChange={(event) => void updateNormalMode(event.target.checked)}
            />
            <span>
              <b>Đưa TV về chế độ bình thường</b>
              <small>{normalMode ? "Launcher Google TV đang được phép" : "Phước Hào TV đang giữ chế độ kiosk"}</small>
            </span>
          </label>
          {can(user, 'dashboard.view') && <NavLink className="primary" to="/admin/dashboard?tv=1">
            <MonitorPlay /> Mở màn hình TV
          </NavLink>}
        </div>
      </div>

      {canPublish && <section className="tv-connect-card" aria-label="Kết nối màn hình TV">
        <div className="tv-connect-copy">
          <h2><MonitorPlay /> Kết nối màn hình TV</h2>
          <p>Quét QR để kết nối ngay, hoặc nhập mã 6 ký tự trên TV rồi bấm Kết nối TV.</p>
        </div>
        <form className="tv-connect-form" onSubmit={submitPairCode}>
          <label htmlFor="tv-pair-code">Mã hiển thị trên TV</label>
          <div className="tv-connect-controls">
            <input id="tv-pair-code" value={pairCode} onChange={event => { setPairCode(event.target.value.toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, "").slice(0,6)); setPairError(""); setPairMessage(""); }} placeholder="VD: AB3D5F" autoComplete="one-time-code" inputMode="text" maxLength={6} aria-describedby="tv-pair-feedback" />
            <button type="button" className="secondary" onClick={() => void (pairScanning ? stopPairScanner().then(() => setPairScanning(false)) : startPairScan())} disabled={pairBusy}><ScanLine /> {pairScanning ? "Đóng camera" : "Quét QR"}</button>
            <button className="primary" disabled={pairBusy || pairCode.length !== 6}>{pairBusy ? "Đang kết nối…" : "Kết nối TV"}</button>
          </div>
          <div id="tv-pair-feedback" aria-live="polite">{pairError && <p className="error">{pairError}</p>}{pairMessage && <p className="tv-connect-success">{pairMessage}</p>}</div>
        </form>
        {pairScanning && <div id="tv-pair-reader" className="tv-connect-reader" />}
      </section>}

      <TvAudioSettings />
      <div className="tv-admin-layout">
        <aside className="tv-scenario-sidebar">
          <h2>Kịch bản</h2>
          {canManage && <form onSubmit={createScenario}>
            <input
              value={newName}
              maxLength={120}
              placeholder="Tên kịch bản mới"
              onChange={(event) => setNewName(event.target.value)}
            />
            <button className="primary" disabled={!newName.trim() || Boolean(busy)}>
              <Plus /> Tạo
            </button>
          </form>}
          <div className="tv-scenario-list">
            {scenarios.map((scenario) => (
              <button
                key={scenario.id}
                className={scenario.id === selectedId ? "active" : ""}
                onClick={() => chooseScenario(scenario.id)}
              >
                <span>
                  <b>{scenario.name}</b>
                  <small>{scenario.items.length} cảnh</small>
                </span>
                {scenario.is_active && <em>ĐANG PHÁT</em>}
              </button>
            ))}
            {!scenarios.length && (
              <div className="tv-empty-list">Chưa có kịch bản nào.</div>
            )}
          </div>
        </aside>

        <main className="tv-scenario-editor">
          {draft ? (
            <>
              <div className="tv-editor-head">
                <label>
                  Tên kịch bản
                  <input
                    value={draft.name}
                    readOnly={!canManage}
                    maxLength={120}
                    onChange={(event) =>
                      setDraft({ ...draft, name: event.target.value })
                    }
                  />
                </label>
                <div>
                  <button className="secondary" onClick={() => setPreview(true)}>
                    <Maximize2 /> Xem thử
                  </button>
                  {canManage && <button className="secondary" onClick={() => void save()} disabled={Boolean(busy) || (!!draft.is_active && !canPublish)}>
                    <Save /> Lưu
                  </button>}
                  {canPublish && <button className="primary" onClick={() => void activate()} disabled={Boolean(busy)}>
                    <CheckCircle2 />
                    {draft.is_active ? "Lưu & cập nhật TV" : "Kích hoạt"}
                  </button>}
                  {canManage && !draft.is_active && (
                    <button className="danger-soft" onClick={() => void removeScenario()} disabled={Boolean(busy)}>
                      <Trash2 />
                    </button>
                  )}
                </div>
              </div>

              <fieldset className="permission-fieldset" disabled={!canManage}>
              <div className="tv-add-scene">
                <span>Thêm cảnh:</span>
                {(Object.keys(itemLabels) as TvItemType[]).map((type) => (
                  <button key={type} onClick={() => addItem(type)}>
                    {itemIcons[type]} {itemLabels[type]}
                  </button>
                ))}
              </div>

              <div className="tv-scene-editor-list">
                {draft.items.map((item, position) => (
                  <article key={item.key || item.id || position} className={`tv-scene-editor-card ${item.type.toLowerCase()}`}>
                    <div className="tv-scene-number">{position + 1}</div>
                    <div className="tv-scene-form">
                      <div className="tv-scene-card-title">
                        <span>{itemIcons[item.type]}</span>
                        <div>
                          <b>{itemLabels[item.type]}</b>
                          <small>{item.type}</small>
                        </div>
                      </div>
                      <div className="tv-scene-basic-fields">
                        <label>
                          Tiêu đề cảnh
                          <input
                            value={item.title}
                            maxLength={120}
                            onChange={(event) =>
                              updateItem(position, { title: event.target.value })
                            }
                          />
                        </label>
                        <label>
                          Thời lượng (giây)
                          <input
                            type="number"
                            min={item.type === "VIDEO" ? 0 : 5}
                            max={3600}
                            value={item.durationSeconds}
                            onChange={(event) =>
                              updateItem(position, {
                                durationSeconds: Number(event.target.value),
                              })
                            }
                          />
                          {item.type === "VIDEO" && <small>0 = phát hết clip</small>}
                        </label>
                      </div>

                      {item.type === "VIDEO" && (
                        <div className="tv-config-grid">
                          <label>
                            Chọn clip
                            <select
                              value={item.config.mediaId || ""}
                              onChange={(event) =>
                                updateConfig(position, { mediaId: event.target.value })
                              }
                            >
                              {media.filter((entry) => entry.media_type === "VIDEO").map((entry) => (
                                <option key={entry.id} value={entry.id}>{entry.original_name}</option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Hiển thị
                            <select value={item.config.fit || "CONTAIN"} onChange={(event) => updateConfig(position, { fit: event.target.value })}>
                              <option value="CONTAIN">Vừa màn hình, không cắt</option>
                              <option value="COVER">Phủ kín màn hình</option>
                            </select>
                          </label>
                          <label className="tv-check-label">
                            <input type="checkbox" checked={Boolean(item.config.muted)} onChange={(event) => updateConfig(position, { muted: event.target.checked })} />
                            Tắt tiếng clip
                          </label>
                        </div>
                      )}

                      {item.type === "SLIDESHOW" && (
                        <div className="tv-slideshow-config">
                          <div className="tv-config-grid">
                            <label>
                              Mỗi ảnh (giây)
                              <input type="number" min={2} max={60} value={item.config.secondsPerImage || 6} onChange={(event) => updateConfig(position, { secondsPerImage: Number(event.target.value) })} />
                            </label>
                            <label>
                              Hiệu ứng
                              <select value={item.config.effect || "FADE"} onChange={(event) => updateConfig(position, { effect: event.target.value })}>
                                <option value="FADE">Mờ chuyển cảnh</option>
                                <option value="ZOOM">Zoom nhẹ</option>
                                <option value="SLIDE">Trượt ngang</option>
                              </select>
                            </label>
                            <label>
                              Hiển thị
                              <select value={item.config.fit || "CONTAIN"} onChange={(event) => updateConfig(position, { fit: event.target.value })}>
                                <option value="CONTAIN">Vừa màn hình</option>
                                <option value="COVER">Phủ kín</option>
                              </select>
                            </label>
                          </div>
                          <div className="tv-image-picker">
                            {media.filter((entry) => entry.media_type === "IMAGE").map((entry) => {
                              const checked = (item.config.mediaIds || []).includes(entry.id);
                              return (
                                <label key={entry.id} className={checked ? "selected" : ""}>
                                  <img src={entry.url} alt="" />
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const current = item.config.mediaIds || [];
                                      updateConfig(position, {
                                        mediaIds: checked
                                          ? current.filter((id: string) => id !== entry.id)
                                          : [...current, entry.id],
                                      });
                                    }}
                                  />
                                  <span>{entry.original_name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {item.type === "LED" && (
                        <div className="tv-led-config">
                          <label>
                            Nội dung chạy
                            <textarea
                              rows={3}
                              maxLength={300}
                              value={item.config.text || ""}
                              onChange={(event) => updateConfig(position, { text: event.target.value })}
                            />
                          </label>
                          <div className="tv-led-presets">
                            <div className="tv-led-presets-head">
                              <span>Phối màu gợi ý</span>
                              <small>Chọn một mẫu hoặc tùy chỉnh màu bên dưới</small>
                            </div>
                            <div className="tv-led-preset-grid">
                              {ledColorPresets.map((preset) => {
                                const selected =
                                  String(item.config.color || "#ffd43b").toLowerCase() === preset.color &&
                                  String(item.config.background || "#02070a").toLowerCase() === preset.background;
                                return (
                                  <button
                                    key={preset.id}
                                    type="button"
                                    className={selected ? "selected" : ""}
                                    aria-pressed={selected}
                                    title={`${preset.name}: ${preset.description}`}
                                    onClick={() =>
                                      updateConfig(position, {
                                        color: preset.color,
                                        background: preset.background,
                                      })
                                    }
                                  >
                                    <span
                                      className="tv-led-preset-swatch"
                                      style={{
                                        color: preset.color,
                                        background: preset.background,
                                      }}
                                    >
                                      PHƯỚC HÀO
                                    </span>
                                    <span className="tv-led-preset-copy">
                                      <b>{preset.name}</b>
                                      <small>{preset.description}</small>
                                    </span>
                                    {selected && <CheckCircle2 className="tv-led-preset-check" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="tv-config-grid">
                            <label>
                              Màu chữ
                              <input type="color" value={item.config.color || "#ffd43b"} onChange={(event) => updateConfig(position, { color: event.target.value })} />
                            </label>
                            <label>
                              Màu nền
                              <input type="color" value={item.config.background || "#02070a"} onChange={(event) => updateConfig(position, { background: event.target.value })} />
                            </label>
                            <label>
                              Tốc độ
                              <select value={item.config.speed || "NORMAL"} onChange={(event) => updateConfig(position, { speed: event.target.value })}>
                                <option value="SLOW">Chậm</option>
                                <option value="NORMAL">Vừa</option>
                                <option value="FAST">Nhanh</option>
                              </select>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="tv-scene-actions">
                      <button title="Đưa lên" disabled={position === 0} onClick={() => moveItem(position, -1)}><ArrowUp /></button>
                      <button title="Đưa xuống" disabled={position === draft.items.length - 1} onClick={() => moveItem(position, 1)}><ArrowDown /></button>
                      <button className="danger-text" title="Xóa cảnh" disabled={draft.items.length === 1} onClick={() => setDraft({ ...draft, items: draft.items.filter((_, itemIndex) => itemIndex !== position) })}><Trash2 /></button>
                    </div>
                  </article>
                ))}
              </div>
              </fieldset>
            </>
          ) : (
            <div className="tv-editor-empty">
              <MonitorPlay />
              <h2>Tạo kịch bản đầu tiên</h2>
              <p>Kịch bản mới sẽ có sẵn màn hình tổng quan realtime.</p>
            </div>
          )}
        </main>
      </div>

      <section className="tv-media-library">
        <div className="card-title">
          <div>
            <h2>Thư viện ảnh & video</h2>
            <p>Ảnh JPG/PNG/WebP/GIF; video MP4/WebM/MOV, tối đa 250 MB mỗi tệp.</p>
          </div>
          {canMedia && <form onSubmit={upload}>
            <label className="secondary tv-file-button">
              <Upload /> {uploadFile ? uploadFile.name : "Chọn tệp"}
              <input
                key={uploadFile ? `${uploadFile.name}-${uploadFile.lastModified}` : "empty"}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
              />
            </label>
            <button className="primary" disabled={!uploadFile || Boolean(busy)}>
              {busy === "upload" ? <LoaderCircle className="spin" /> : <Upload />}
              Tải lên
            </button>
          </form>}
        </div>
        <div className="tv-media-grid">
          {media.map((entry) => (
            <article key={entry.id}>
              <div className="tv-media-preview">
                {entry.media_type === "IMAGE" ? (
                  <img src={entry.url} alt={entry.original_name} />
                ) : (
                  <video src={entry.url} muted preload="metadata" />
                )}
                <span>{entry.media_type === "IMAGE" ? <ImageIcon /> : <Film />}</span>
              </div>
              <div>
                <b title={entry.original_name}>{entry.original_name}</b>
                <small>{bytes(entry.file_size)}</small>
              </div>
              {canMedia && <button className="danger-text" onClick={() => void removeMedia(entry)} disabled={Boolean(busy)}><Trash2 /></button>}
            </article>
          ))}
          {!media.length && <div className="tv-empty-list">Chưa có ảnh hoặc video.</div>}
        </div>
      </section>

      {(error || notice) && (
        <div className={`tv-save-message ${error ? "error" : "success"}`}>
          {error || notice}
        </div>
      )}

      <TvPlaybackLayer
        active={preview}
        scenario={draft ? { ...draft, media } : null}
        preview
        onExit={() => setPreview(false)}
      />
    </div>
  );
}
